import { useFormContext } from 'react-hook-form';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormContainer } from 'components/layout';
import { Box } from '@mui/material';
import {
  AddPackTagButton,
  ChipInput,
  PacksWithCreatives,
  SelectableCreativesGrid,
} from 'components/common';
import type { GridColDef } from '@mui/x-data-grid';
import { useRowsCreatives } from 'features/creatives';
import type { CreativeEntry, PackEntry } from 'types/shared';
import { CREATIVES_LIMIT, Network, TikTokAdType } from '../../../constants';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';

const COLUMNS: GridColDef<CreativeEntry>[] = [
  { field: 'creative_id', headerName: 'Creative ID', width: 100 },
  { field: 'name', headerName: 'Name', width: 450 },
  { field: 'app', headerName: 'App', width: 100 },
  { field: 'creative_type', headerName: 'Type', width: 100 },
  { field: 'created', headerName: 'Created', width: 100 },
];

const ChooseCreative = memo(() => {
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const adType: string = watch('adType');
  const selectedCreatives: string[] = watch('selectedCreatives');
  const selectedOnly = watch('selectedCreativesOnly') as boolean;
  const packsWithCreatives: PackEntry[] = watch('packsWithCreatives');
  const activePacksWithCreatives: number = watch('activePacksWithCreatives');
  const isCreativesFieldError: string = errors?.selectedCreatives?.message as string;
  const { setCreativesByPack: setCreativesByPackParam } = useLocationParams();

  const { rows: creativesList, isLoading, isError, error } = useRowsCreatives({
    params: {
      ad_type: adType ?? TikTokAdType.SINGLE_VIDEO,
      network: Network.TIKTOK,
    },
  });

  const filteredRows = useMemo(() => {
    if (!selectedOnly || !creativesList) return creativesList;
    return creativesList.filter((row) => selectedCreatives.includes(row.id));
  }, [creativesList, selectedCreatives, selectedOnly]);

  const onSelectionModelChange = useCallback(
    (parsedSelectedRows: string[]) => {
      if (!creativesList?.length) return;

      const updatedPacks = packsWithCreatives.map((pack, index) =>
        index === activePacksWithCreatives
          ? { ...pack, creatives: parsedSelectedRows }
          : pack,
      );

      const selectedCreativesRows = creativesList.filter((creative) =>
        parsedSelectedRows.includes(creative.id),
      );

      setValue('packsWithCreatives', updatedPacks, { shouldValidate: true });
      setValue('selectedCreativesRows', selectedCreativesRows, { shouldValidate: true });
      setValue('selectedCreatives', parsedSelectedRows, { shouldValidate: true });
    },
    [creativesList, packsWithCreatives, activePacksWithCreatives, setValue],
  );

  useEffect(() => {
    void trigger('packsWithCreatives');
    void trigger('selectedCreatives');
  }, [trigger]);

  const hasWrittenCreativesRef = useRef(false);

  useEffect(() => {
    const hasCreatives = packsWithCreatives.some((pack) =>
      (pack.creatives ?? []).some((id) => typeof id === 'string' && id.length > 0),
    );

    // First paint can still have empty packs while a deep-linked `creatives`
    // param is being restored. Writing that empty list would clear the URL.
    if (!hasCreatives && !hasWrittenCreativesRef.current) return;

    hasWrittenCreativesRef.current = true;
    setCreativesByPackParam(
      packsWithCreatives.map((pack) => ({
        tag: pack.tag,
        creatives: pack.creatives,
      })),
    );
  }, [packsWithCreatives, setCreativesByPackParam]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    datadogActions.trackDatadogError(error as Error, {
      source: 'tiktok_choose_creative_query',
      adType: String(adType ?? TikTokAdType.SINGLE_VIDEO),
      network: Network.TIKTOK,
    });
  }, [isError, error, adType]);

  return (
    <FormContainer
      title="Select creatives"
      error={isCreativesFieldError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <PacksWithCreatives />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: '50%' }}>
            <ChipInput
              name={`packsWithCreatives.${activePacksWithCreatives}.filterCreativeByName`}
              inputLabel="Search by name" />
          </Box>
          <Box sx={{ minHeight: 32, mt: 0.5 }}>
            <AddPackTagButton />
          </Box>
        </Box>
      </Box>

      <SelectableCreativesGrid
        rows={filteredRows ?? []}
        columns={COLUMNS}
        selectedIds={selectedCreatives}
        onSelectionChange={onSelectionModelChange}
        limit={CREATIVES_LIMIT}
        isLoading={isLoading}
        totalCount={creativesList?.length ?? 0}
        mountGuardKey={activePacksWithCreatives}
      />
    </FormContainer>
  );
});

ChooseCreative.displayName = 'ChooseCreative';

export default ChooseCreative;